import { useLayoutEffect, type RefObject } from 'react';

export interface FitTextOptions {
  /** Allowed margin between the lettering and the box edges, as fractions of the box height. */
  minMargin: number;
  maxMargin: number;
  /** Ink outside the glyphs, in em: outline width beyond the glyph edge, and shadow drop below. */
  outlineEm: number;
  shadowEm: number;
  /** Strongest horizontal condensing used when the copy is long. */
  minScaleX: number;
}

interface FontMetrics {
  /** Cap height, font ascent and descent as fractions of the font size. */
  cap: number;
  ascent: number;
  descent: number;
}

function fontMetrics(fontFamily: string, sample: string): FontMetrics {
  const ctx = document.createElement('canvas').getContext('2d');
  if (!ctx) return { cap: 0.7, ascent: 0.9, descent: 0.25 };
  ctx.font = `100px ${fontFamily}`;
  const m = ctx.measureText(sample);
  return {
    cap: m.actualBoundingBoxAscent / 100,
    ascent: m.fontBoundingBoxAscent / 100,
    descent: m.fontBoundingBoxDescent / 100,
  };
}

/**
 * Fits single-line capitals into a box with equal margins on all sides, keeping the font's own
 * letter spacing. Ink width and height both grow linearly with font size, so there is one size
 * where the side margins equal the top and bottom margins; the text is set at that size.
 * - Short copy, whose equal margin would fall below `minMargin`, uses `minMargin` top and
 *   bottom and is centred with wider side margins.
 * - Long copy, whose equal margin would exceed `maxMargin` (small text), uses `maxMargin` and is
 *   condensed horizontally, down to `minScaleX`; beyond that the font shrinks.
 * Re-fits on resize and once web fonts load. The text element must be absolutely positioned in
 * the box, single-line, with line-height 1.
 */
export function useFitText(
  boxRef: RefObject<HTMLElement | null>,
  textRef: RefObject<HTMLElement | null>,
  options: FitTextOptions,
  copy: string,
) {
  const { minMargin, maxMargin, outlineEm, shadowEm, minScaleX } = options;

  useLayoutEffect(() => {
    const box = boxRef.current;
    const text = textRef.current;
    if (!box || !text) return;

    const fit = () => {
      const width = box.clientWidth;
      const height = box.clientHeight;
      if (width === 0 || height === 0) return;
      const metrics = fontMetrics(getComputedStyle(text).fontFamily, copy.toUpperCase());

      // Ink size per pixel of font size. `scale` does not affect layout, so offsetWidth is the
      // natural, unscaled width.
      const reference = 100;
      text.style.fontSize = `${reference}px`;
      const inkWidthPerPx = text.offsetWidth / reference + 2 * outlineEm;
      const inkHeightPerPx = outlineEm + metrics.cap + outlineEm + shadowEm;

      // Equal margins: width - 2m = w * f and height - 2m = h * f. Short copy (narrow ink)
      // solves to a large font and a small or negative margin; long copy to a small font.
      const equalFont = (width - height) / (inkWidthPerPx - inkHeightPerPx);
      const equalMargin = (height - inkHeightPerPx * equalFont) / 2;
      let fontSize = equalFont;
      let scaleX = 1;
      if (!(equalFont > 0) || equalMargin < minMargin * height) {
        // Short copy: full height, centred, wider side margins.
        fontSize = (height - 2 * minMargin * height) / inkHeightPerPx;
      } else if (equalMargin > maxMargin * height) {
        // Long copy: keep the height, condense to fit the width.
        const margin = maxMargin * height;
        fontSize = (height - 2 * margin) / inkHeightPerPx;
        scaleX = Math.min(1, (width - 2 * margin) / (inkWidthPerPx * fontSize));
        if (scaleX < minScaleX) {
          fontSize *= scaleX / minScaleX;
          scaleX = minScaleX;
        }
      }

      // Centre the ink vertically, then place the baseline.
      const top = (height - inkHeightPerPx * fontSize) / 2;
      const baselineY = top + (outlineEm + metrics.cap) * fontSize;
      const baselineInLine =
        ((1 - metrics.ascent - metrics.descent) / 2 + metrics.ascent) * fontSize;

      text.style.fontSize = `${fontSize}px`;
      text.style.top = `${baselineY - baselineInLine}px`;
      text.style.scale = `${scaleX} 1`;
    };

    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(box);
    let active = true;
    void document.fonts.ready.then(() => {
      if (active) fit();
    });
    return () => {
      active = false;
      observer.disconnect();
    };
  }, [boxRef, textRef, minMargin, maxMargin, outlineEm, shadowEm, minScaleX, copy]);
}
